import { Link } from '@tanstack/react-router';
import { Users, BedDouble, MapPin } from 'lucide-react';
import type { PublicApartment } from '@/api/generated';
import type { PartySearchParams } from '@/lib/party';
import { Photo, PriceDisplay } from '@/components/ui';
import { coverPhoto, formatAmenity } from '@/lib/apartments';
import { formatPriceMajor } from '@/lib/money';
import { m } from '@/paraglide/messages';

// ApartmentCard is the apartment-grid tile, reskinned onto the Ollio DS (1p R4a-2).
// It stays a real <Link> (native navigation + keyboard a11y) styled with the
// elevated-card look — deliberately NOT <Card interactive>, which would put
// role="button" on a div inside the link.
//
// Four price states, in priority order (OH-63 Task 3, revised by the
// whole-branch review IMPORTANT 1 fix):
//   1. perNightMinor present — exact price for the guest's chosen dates
//      (search grid, R4c). Always wins when present.
//   2. otherwise fromPriceMinor is a NUMBER — the cheapest sellable night in
//      the next 90 days (home grid). Renders "from €X / night".
//   3. otherwise fromPriceMinor === null — the server checked and confirmed
//      no sellable night in the window. Renders "Sold out". The card stays
//      a link regardless: a sold-out apartment may have free dates beyond
//      the 90-day window, and its own calendar will show them.
//   4. otherwise (fromPriceMinor === undefined) — the server could not tell
//      (a MinSellableRates lookup error, or a from_price_minor field that
//      doesn't exist yet on the API this client is momentarily talking to —
//      deploy-book-staging.yml/Pages ships before deploy-staging.yml/EC2 off
//      the same push, so there is a real window where a newer frontend
//      talks to an older API). Renders NOTHING: an unknown price must never
//      read as the affirmative claim "Sold out".
//
// fromPriceMinor's true wire type is `number | null | undefined` (see
// api/booking-openapi.yaml) even though the generated PublicApartment DTO
// widens it to `number | undefined` — @hey-api drops the null arm from a
// nullable optional field (same gap `city` has on that DTO). Callers pass
// the raw field straight through un-narrowed; this component's own prop
// type below is the honest one and is what the branches below compare
// against.
export function ApartmentCard({
  apt,
  perNightMinor,
  fromPriceMinor,
  currency,
  sizes,
  search,
}: {
  apt: PublicApartment;
  perNightMinor?: number;
  fromPriceMinor?: number | null;
  currency?: string;
  /**
   * `sizes` attribute for the cover photo. The home and search grids share
   * the same `max-w-6xl` container and column breakpoints, but NOT the same
   * per-card width: search reserves a `w-56` (224px) filter rail + `gap-9`
   * that home doesn't have, so a card is narrower there at every desktop
   * width. Every current call site (index.tsx, search.tsx) passes its own
   * grid's math explicitly — required, not optional, despite the TS `?`;
   * the fallback below exists only so a future call site that forgets to
   * pass one degrades to the home grid's numbers instead of the old
   * over-declaring `33vw` default.
   */
  sizes?: string;
  /**
   * What the guest already told us — dates and party — carried onto the
   * apartment page so the booking widget opens on their search rather than
   * silently resetting to its own defaults. A guest who searched for two
   * adults and a child should not have to say so twice, and (worse) should
   * not get a hold for a party they never chose.
   */
  search?: { checkin?: string; checkout?: string } & PartySearchParams;
}) {
  const cover = coverPhoto(apt);
  const amenities = apt.amenities.slice(0, 3);

  return (
    <Link
      to="/apartment/$id"
      params={{ id: apt.id }}
      search={search ?? {}}
      className="group flex flex-col overflow-hidden rounded-[var(--radius-card)] border border-[var(--border-hairline)] bg-card shadow-[var(--shadow-sm)] transition hover:-translate-y-0.5 hover:shadow-[var(--shadow-lg)] focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-glow)]"
    >
      <div className="aspect-[4/3] w-full overflow-hidden">
        <Photo
          src={cover?.url}
          alt={cover?.alt ?? apt.title}
          width={cover?.width ?? undefined}
          height={cover?.height ?? undefined}
          // Fallback only — index.tsx and search.tsx both pass their own
          // `sizes`. Matches the HOME grid (max-w-6xl px-6, gap-6,
          // grid-cols-1 sm:grid-cols-2 lg:grid-cols-3): above the 1152px
          // container cap a card is a fixed 352px, not a growing 33vw
          // fraction — the old `33vw` default picked the 1440 rung
          // (80 577 B) at 1920px/2x DPR where 960 (53 059 B) would do.
          sizes={
            sizes ??
            '(min-width: 1152px) 352px, (min-width: 1024px) calc((100vw - 96px) / 3), (min-width: 640px) calc((100vw - 72px) / 2), calc(100vw - 48px)'
          }
          seed={apt.title.length}
          className="[&_img]:transition-transform [&_img]:duration-[1200ms] group-hover:[&_img]:scale-[1.04]"
        />
      </div>

      <div className="flex flex-1 flex-col gap-2 p-5">
        <h3 className="font-serif text-xl font-semibold leading-snug tracking-tight text-[var(--ollio-ink)]">
          {apt.title}
        </h3>

        <p className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
          {apt.city ? (
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
              {apt.city}
            </span>
          ) : null}
          <span className="inline-flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5" aria-hidden="true" />
            {m.card_guests({ count: apt.occupancy_max })}
          </span>
          {typeof apt.bedrooms === 'number' ? (
            <span className="inline-flex items-center gap-1.5">
              <BedDouble className="h-3.5 w-3.5" aria-hidden="true" />
              {apt.bedrooms}
            </span>
          ) : null}
        </p>

        {amenities.length > 0 ? (
          <ul className="mt-1 flex flex-wrap gap-1.5">
            {amenities.map((a) => (
              <li
                key={a}
                className="rounded-full border border-border px-2.5 py-0.5 text-xs text-muted"
              >
                {formatAmenity(a)}
              </li>
            ))}
          </ul>
        ) : null}

        {/* Price: perNightMinor is minor units → major for PriceDisplay
            (caller-converts per the kit contract). Falls back to the
            from-price, then an explicit Sold out, then nothing at all for
            an unknown price — see the four-state comment above. */}
        {typeof perNightMinor === 'number' ? (
          <div className="mt-auto pt-2">
            <PriceDisplay amount={perNightMinor / 100} currency={currency ?? apt.currency} per="night" size="sm" />
          </div>
        ) : typeof fromPriceMinor === 'number' ? (
          <div className="mt-auto pt-2 text-sm text-muted">
            {m.card_from_price({
              price: formatPriceMajor(fromPriceMinor / 100, currency ?? apt.currency),
            })}
          </div>
        ) : fromPriceMinor === null ? (
          <div className="mt-auto pt-2 text-sm text-muted">{m.card_sold_out()}</div>
        ) : null}
      </div>
    </Link>
  );
}
